import React, { useState } from 'react'
import { 
  Search, 
  Eye, 
  Monitor, 
  Smartphone, 
  Tablet,
  Globe,
  FileText,
  Image,
  Link,
  Settings,
  CheckCircle,
  AlertCircle,
  Info,
  TrendingUp,
  Share2,
  Tag
} from 'lucide-react'

interface SEOData {
  title: string
  description: string
  keywords: string[]
  author: string
  ogTitle: string
  ogDescription: string
  ogImage: string
  twitterCard: string
  twitterSite: string
  canonical: string
  robots: string
  language: string
  favicon: string
  structuredData: {
    type: string
    name: string
    description: string
    url: string
    image: string
    author: {
      name: string
      url: string
    }
  }
}

interface SEOPanelProps {
  seoData: SEOData
  onUpdate: (data: SEOData) => void
  websiteUrl?: string
  previewMode?: 'desktop' | 'tablet' | 'mobile'
  onPreviewModeChange?: (mode: 'desktop' | 'tablet' | 'mobile') => void
}

export default function SEOPanel({ 
  seoData, 
  onUpdate, 
  websiteUrl,
  previewMode = 'desktop',
  onPreviewModeChange
}: SEOPanelProps) {
  const [activeTab, setActiveTab] = useState<'basic' | 'social' | 'advanced' | 'preview'>('basic')
  const [newKeyword, setNewKeyword] = useState('')

  const handleInputChange = (field: keyof SEOData, value: any) => {
    onUpdate({
      ...seoData,
      [field]: value
    })
  }

  const handleStructuredDataChange = (field: keyof SEOData['structuredData'], value: any) => {
    onUpdate({
      ...seoData,
      structuredData: {
        ...seoData.structuredData,
        [field]: value
      }
    })
  }

  const addKeyword = () => {
    if (newKeyword.trim() && !seoData.keywords.includes(newKeyword.trim())) {
      handleInputChange('keywords', [...seoData.keywords, newKeyword.trim()])
      setNewKeyword('')
    }
  }

  const removeKeyword = (keyword: string) => {
    handleInputChange('keywords', seoData.keywords.filter(k => k !== keyword))
  }

  const generateMetaTags = () => {
    return `
<!-- Primary Meta Tags -->
<title>${seoData.title}</title>
<meta name="title" content="${seoData.title}">
<meta name="description" content="${seoData.description}">
<meta name="keywords" content="${seoData.keywords.join(', ')}">
<meta name="author" content="${seoData.author}">
<meta name="robots" content="${seoData.robots}">
<meta name="language" content="${seoData.language}">
<link rel="canonical" href="${seoData.canonical}">

<!-- Open Graph / Facebook -->
<meta property="og:type" content="website">
<meta property="og:url" content="${seoData.canonical}">
<meta property="og:title" content="${seoData.ogTitle}">
<meta property="og:description" content="${seoData.ogDescription}">
<meta property="og:image" content="${seoData.ogImage}">

<!-- Twitter -->
<meta property="twitter:card" content="${seoData.twitterCard}">
<meta property="twitter:url" content="${seoData.canonical}">
<meta property="twitter:title" content="${seoData.ogTitle}">
<meta property="twitter:description" content="${seoData.ogDescription}">
<meta property="twitter:image" content="${seoData.ogImage}">
<meta property="twitter:site" content="${seoData.twitterSite}">

<!-- Structured Data -->
<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "${seoData.structuredData.type}",
  "name": "${seoData.structuredData.name}",
  "description": "${seoData.structuredData.description}",
  "url": "${seoData.structuredData.url}",
  "image": "${seoData.structuredData.image}",
  "author": {
    "@type": "Person",
    "name": "${seoData.structuredData.author.name}",
    "url": "${seoData.structuredData.author.url}"
  }
}
</script>
    `.trim()
  }

  const getSEOScore = () => {
    let score = 0
    let maxScore = 0

    // Basic SEO
    maxScore += 20
    if (seoData.title && seoData.title.length >= 30 && seoData.title.length <= 60) score += 20
    else if (seoData.title) score += 10

    maxScore += 20
    if (seoData.description && seoData.description.length >= 120 && seoData.description.length <= 160) score += 20
    else if (seoData.description) score += 10

    maxScore += 10
    if (seoData.keywords.length >= 3) score += 10
    else if (seoData.keywords.length > 0) score += 5

    // Social Media
    maxScore += 15
    if (seoData.ogTitle && seoData.ogDescription && seoData.ogImage) score += 15
    else if (seoData.ogTitle || seoData.ogDescription) score += 8

    // Technical
    maxScore += 10
    if (seoData.canonical) score += 10

    maxScore += 10
    if (seoData.favicon) score += 10

    maxScore += 10
    if (seoData.structuredData.type && seoData.structuredData.name) score += 10

    // Language and Author
    maxScore += 5
    if (seoData.language && seoData.author) score += 5

    return Math.round((score / maxScore) * 100)
  }

  const seoScore = getSEOScore()
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100'
    if (score >= 60) return 'bg-yellow-100'
    return 'bg-red-100'
  }

  const renderBasicTab = () => (
    <div className="space-y-6">
      {/* SEO Score */}
      <div className={`${getScoreBgColor(seoScore)} rounded-lg p-4`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">SEO Score</h3>
          <span className={`text-2xl font-bold ${getScoreColor(seoScore)}`}>
            {seoScore}/100
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full ${
              seoScore >= 80 ? 'bg-green-500' : seoScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${seoScore}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          {seoScore >= 80 ? 'Excellent SEO optimization!' : 
           seoScore >= 60 ? 'Good, but could be improved' : 
           'Needs significant improvement'}
        </p>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Page Title *
        </label>
        <input
          type="text"
          value={seoData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="Enter your page title (30-60 characters)"
          maxLength={60}
        />
        <div className="flex justify-between items-center mt-1">
          <span className="text-xs text-gray-500">
            {seoData.title.length}/60 characters
          </span>
          {seoData.title.length >= 30 && seoData.title.length <= 60 ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-500" />
          )}
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meta Description *
        </label>
        <textarea
          value={seoData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="Describe your page (120-160 characters)"
          maxLength={160}
        />
        <div className="flex justify-between items-center mt-1">
          <span className="text-xs text-gray-500">
            {seoData.description.length}/160 characters
          </span>
          {seoData.description.length >= 120 && seoData.description.length <= 160 ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-500" />
          )}
        </div>
      </div>

      {/* Keywords */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Keywords
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {seoData.keywords.map((keyword) => (
            <span
              key={keyword}
              className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
            >
              {keyword}
              <button
                onClick={() => removeKeyword(keyword)}
                className="ml-2 text-purple-600 hover:text-purple-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex">
          <input
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Add a keyword"
          />
          <button
            onClick={addKeyword}
            className="px-4 py-2 bg-purple-600 text-white rounded-r-lg hover:bg-purple-700"
          >
            Add
          </button>
        </div>
      </div>

      {/* Author */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Author
        </label>
        <input
          type="text"
          value={seoData.author}
          onChange={(e) => handleInputChange('author', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="Author name"
        />
      </div>

      {/* Language */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Language
        </label>
        <select
          value={seoData.language}
          onChange={(e) => handleInputChange('language', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="en">English</option>
          <option value="es">Spanish</option>
          <option value="fr">French</option>
          <option value="de">German</option>
          <option value="it">Italian</option>
          <option value="pt">Portuguese</option>
          <option value="ru">Russian</option>
          <option value="zh">Chinese</option>
          <option value="ja">Japanese</option>
          <option value="ko">Korean</option>
        </select>
      </div>
    </div>
  )

  const renderSocialTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Social Media Optimization</h4>
            <p className="text-blue-800 text-sm">
              These settings control how your website appears when shared on social media platforms.
            </p>
          </div>
        </div>
      </div>

      {/* Open Graph Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Open Graph Title
        </label>
        <input
          type="text"
          value={seoData.ogTitle}
          onChange={(e) => handleInputChange('ogTitle', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="Title for social media sharing"
        />
      </div>

      {/* Open Graph Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Open Graph Description
        </label>
        <textarea
          value={seoData.ogDescription}
          onChange={(e) => handleInputChange('ogDescription', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="Description for social media sharing"
        />
      </div>

      {/* Open Graph Image */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Social Media Image
        </label>
        <input
          type="url"
          value={seoData.ogImage}
          onChange={(e) => handleInputChange('ogImage', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="https://example.com/image.jpg (1200x630px recommended)"
        />
      </div>

      {/* Twitter Card */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Twitter Card Type
        </label>
        <select
          value={seoData.twitterCard}
          onChange={(e) => handleInputChange('twitterCard', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="summary">Summary</option>
          <option value="summary_large_image">Summary with Large Image</option>
          <option value="app">App</option>
          <option value="player">Player</option>
        </select>
      </div>

      {/* Twitter Site */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Twitter Site Handle
        </label>
        <input
          type="text"
          value={seoData.twitterSite}
          onChange={(e) => handleInputChange('twitterSite', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="@yourhandle"
        />
      </div>
    </div>
  )

  const renderAdvancedTab = () => (
    <div className="space-y-6">
      {/* Canonical URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Canonical URL
        </label>
        <input
          type="url"
          value={seoData.canonical}
          onChange={(e) => handleInputChange('canonical', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="https://yourwebsite.com"
        />
      </div>

      {/* Robots */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Robots Meta Tag
        </label>
        <select
          value={seoData.robots}
          onChange={(e) => handleInputChange('robots', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="index, follow">Index, Follow</option>
          <option value="noindex, follow">No Index, Follow</option>
          <option value="index, nofollow">Index, No Follow</option>
          <option value="noindex, nofollow">No Index, No Follow</option>
        </select>
      </div>

      {/* Favicon */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Favicon URL
        </label>
        <input
          type="url"
          value={seoData.favicon}
          onChange={(e) => handleInputChange('favicon', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="https://yourwebsite.com/favicon.ico"
        />
      </div>

      {/* Structured Data */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Structured Data Type
        </label>
        <select
          value={seoData.structuredData.type}
          onChange={(e) => handleStructuredDataChange('type', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="WebPage">Web Page</option>
          <option value="Article">Article</option>
          <option value="BlogPosting">Blog Post</option>
          <option value="Product">Product</option>
          <option value="Organization">Organization</option>
          <option value="Person">Person</option>
          <option value="Event">Event</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Structured Data Name
        </label>
        <input
          type="text"
          value={seoData.structuredData.name}
          onChange={(e) => handleStructuredDataChange('name', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="Page or content name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Structured Data URL
        </label>
        <input
          type="url"
          value={seoData.structuredData.url}
          onChange={(e) => handleStructuredDataChange('url', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="https://yourwebsite.com"
        />
      </div>
    </div>
  )

  const renderPreviewTab = () => (
    <div className="space-y-6">
      {/* Device Preview */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Preview Device
        </label>
        <div className="flex space-x-2">
          <button
            onClick={() => onPreviewModeChange?.('desktop')}
            className={`p-3 rounded-lg border-2 transition-all duration-200 ${
              previewMode === 'desktop'
                ? 'border-purple-500 bg-purple-50 text-purple-700'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Monitor className="w-5 h-5" />
          </button>
          <button
            onClick={() => onPreviewModeChange?.('tablet')}
            className={`p-3 rounded-lg border-2 transition-all duration-200 ${
              previewMode === 'tablet'
                ? 'border-purple-500 bg-purple-50 text-purple-700'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Tablet className="w-5 h-5" />
          </button>
          <button
            onClick={() => onPreviewModeChange?.('mobile')}
            className={`p-3 rounded-lg border-2 transition-all duration-200 ${
              previewMode === 'mobile'
                ? 'border-purple-500 bg-purple-50 text-purple-700'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Smartphone className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Search Engine Preview */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Search Engine Preview</h3>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-blue-600 text-lg hover:underline cursor-pointer">
            {seoData.title || 'Your page title will appear here'}
          </div>
          <div className="text-green-700 text-sm">
            {websiteUrl || 'https://yourwebsite.com'}
          </div>
          <div className="text-gray-600 text-sm mt-1">
            {seoData.description || 'Your meta description will appear here'}
          </div>
        </div>
      </div>

      {/* Social Media Preview */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Social Media Preview</h3>
        <div className="border border-gray-200 rounded-lg p-4 bg-white max-w-md">
          {seoData.ogImage && (
            <img
              src={seoData.ogImage}
              alt="Social media preview"
              className="w-full h-32 object-cover rounded mb-3"
            />
          )}
          <div className="text-sm text-gray-500 mb-1">yourwebsite.com</div>
          <div className="font-semibold text-gray-900 mb-1">
            {seoData.ogTitle || seoData.title || 'Your page title'}
          </div>
          <div className="text-sm text-gray-600">
            {seoData.ogDescription || seoData.description || 'Your description'}
          </div>
        </div>
      </div>

      {/* Generated Meta Tags */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Generated Meta Tags</h3>
        <div className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto">
          <pre className="text-xs whitespace-pre-wrap">
            {generateMetaTags()}
          </pre>
        </div>
      </div>
    </div>
  )

  const tabs = [
    { id: 'basic', label: 'Basic SEO', icon: <Search className="w-4 h-4" /> },
    { id: 'social', label: 'Social Media', icon: <Share2 className="w-4 h-4" /> },
    { id: 'advanced', label: 'Advanced', icon: <Settings className="w-4 h-4" /> },
    { id: 'preview', label: 'Preview', icon: <Eye className="w-4 h-4" /> }
  ]

  return (
    <div className="bg-white rounded-2xl shadow-lg">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">SEO & Optimization</h2>
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getScoreBgColor(seoScore)} ${getScoreColor(seoScore)}`}>
            <TrendingUp className="w-4 h-4 mr-1" />
            SEO Score: {seoScore}/100
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span className="ml-2">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'basic' && renderBasicTab()}
        {activeTab === 'social' && renderSocialTab()}
        {activeTab === 'advanced' && renderAdvancedTab()}
        {activeTab === 'preview' && renderPreviewTab()}
      </div>
    </div>
  )
}
